/**
 * ─── HRIS Complete: Income File, Authorization, Bank Export, DOLE Sheet ──
 * Final PayrollProSolutions features for 100% parity.
 */

import { useEffect, useState, useCallback } from 'react'
import { Card, CardHeader, Badge, Button, Input, Modal } from '@repo/ui-core'
import { employeeRepo, payrollRepo } from '../../lib/db'
import { FileText, Download, CheckCircle, XCircle, Clock, Building2, Printer, Plus } from 'lucide-react'

// ═══════════════════════════════════════════════════════════════
// 1. Income File — Per-employee income tracking
// ═══════════════════════════════════════════════════════════════

export function HrisIncomeFilePage() {
  const [employees, setEmployees] = useState<any[]>([])
  const [payrolls, setPayrolls] = useState<any[]>([])
  const [selectedEmp, setSelectedEmp] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [form, setForm] = useState({ incomeType: 'Basic Pay', amount: 0, date: '', notes: '' })
  const [incomeRecords, setIncomeRecords] = useState<any[]>([])

  useEffect(() => {
    ;(async () => {
      try {
        const [e, p] = await Promise.all([employeeRepo.findMany({ page: 1, pageSize: 500 }), payrollRepo.findMany({ page: 1, pageSize: 500 })])
        setEmployees('items' in e ? e.items : [])
        setPayrolls('items' in p ? p.items : [])
      } catch { }
    })()
    try { setIncomeRecords(JSON.parse(localStorage.getItem('hris_income_file') || '[]')) } catch { }
  }, [])

  function addIncome() {
    const emp = employees.find((e: any) => e.id === selectedEmp)
    const entry = { id: Date.now().toString(), employeeId: selectedEmp, employeeName: emp?.fullName ?? emp?.name ?? '', ...form, amount: Number(form.amount), date: form.date || new Date().toISOString().slice(0, 10) }
    const updated = [entry, ...incomeRecords]
    setIncomeRecords(updated)
    localStorage.setItem('hris_income_file', JSON.stringify(updated))
    setShowModal(false)
    setForm({ incomeType: 'Basic Pay', amount: 0, date: '', notes: '' })
  }

  const filtered = selectedEmp ? incomeRecords.filter((r: any) => r.employeeId === selectedEmp) : incomeRecords
  const totalIncome = filtered.reduce((s: number, r: any) => s + (r.amount ?? 0), 0)

  const incomeTypes = ['Basic Pay', 'Overtime', 'Holiday Pay', 'Night Differential', 'Commission', 'Honorarium', 'Allowance', 'Bonus', '13th Month', 'Other']

  return (
    <div className="p-6">
      <div className="mb-6"><h2 className="text-2xl font-bold text-gray-900">Income File</h2><p className="text-sm text-gray-500 mt-1">Per-employee income tracking — cloned from PayrollProSolutions frmIncomeFile</p></div>
      <div className="mb-6 flex gap-3 items-end">
        <div className="flex-1 max-w-xs"><label className="block text-sm font-medium text-gray-700 mb-1">Employee</label><select value={selectedEmp} onChange={e => setSelectedEmp(e.target.value)} className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"><option value="">All Employees</option>{employees.map((e:any)=><option key={e.id} value={e.id}>{e.fullName ?? e.name}</option>)}</select></div>
        <Button onClick={() => setShowModal(true)} icon={<Plus className="h-4 w-4" />}>Add Income</Button>
      </div>
      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-3">
        <div className="rounded-xl border border-green-200 bg-green-50 p-4"><p className="text-xs text-green-600">Total Records</p><p className="mt-1 text-xl font-bold text-green-700">{filtered.length}</p></div>
        <div className="rounded-xl border border-blue-200 bg-blue-50 p-4"><p className="text-xs text-blue-600">Total Income</p><p className="mt-1 text-xl font-bold text-blue-700">₱{totalIncome.toLocaleString()}</p></div>
      </div>
      <Card><CardHeader title="Income Records" description={selectedEmp ? `Filtered by employee` : `All employees`} />
        <div className="overflow-x-auto"><table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50"><tr>{['Date','Employee','Income Type','Amount','Notes'].map(h=><th key={h} className="px-3 py-2.5 text-left text-xs font-medium uppercase text-gray-500">{h}</th>)}</tr></thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {filtered.length === 0 ? <tr><td colSpan={5} className="py-8 text-center text-gray-400">No income records.</td></tr> :
             filtered.map((r: any) => (<tr key={r.id} className="hover:bg-gray-50"><td className="px-3 py-2.5 text-xs">{r.date}</td><td className="px-3 py-2.5 font-medium">{r.employeeName}</td><td className="px-3 py-2.5"><Badge color="blue">{r.incomeType}</Badge></td><td className="px-3 py-2.5 text-right font-bold">₱{(r.amount??0).toLocaleString()}</td><td className="px-3 py-2.5 text-xs text-gray-500">{r.notes || '—'}</td></tr>))}
          </tbody>
        </table></div>
      </Card>
      <Modal open={showModal} onClose={() => setShowModal(false)} title="Add Income" size="sm" footer={<div className="flex gap-2"><Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button><Button onClick={addIncome} icon={<FileText className="h-4 w-4"/>}>Add</Button></div>}>
        <div className="space-y-3">
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Income Type</label><select value={form.incomeType} onChange={e => setForm({...form, incomeType: e.target.value})} className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">{incomeTypes.map(t=><option key={t} value={t}>{t}</option>)}</select></div>
          <Input label="Amount (₱)" type="number" value={String(form.amount)} onChange={e => setForm({...form, amount: Number(e.target.value)})} />
          <Input label="Date" type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} />
          <Input label="Notes" value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} />
        </div>
      </Modal>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════
// 2. Authorization / Approval Workflow
// ═══════════════════════════════════════════════════════════════

export function HrisApprovalPage() {
  const [requests, setRequests] = useState<any[]>([])
  const [showModal, setShowModal] = useState(false)
  const [form, setForm] = useState({ type: 'Payroll', description: '', requestedBy: 'admin' })

  useEffect(() => {
    try { setRequests(JSON.parse(localStorage.getItem('hris_approvals') || '[]')) } catch { }
  }, [])

  function submit() {
    const entry = { id: Date.now().toString(), ...form, status: 'pending', createdAt: new Date().toISOString(), approvedBy: '', approvedAt: '' }
    const updated = [entry, ...requests]
    setRequests(updated)
    localStorage.setItem('hris_approvals', JSON.stringify(updated))
    setShowModal(false)
    setForm({ type: 'Payroll', description: '', requestedBy: 'admin' })
  }

  function approve(id: string) {
    const updated = requests.map(r => r.id === id ? { ...r, status: 'approved', approvedBy: 'admin', approvedAt: new Date().toISOString() } : r)
    setRequests(updated)
    localStorage.setItem('hris_approvals', JSON.stringify(updated))
  }

  function reject(id: string) {
    const updated = requests.map(r => r.id === id ? { ...r, status: 'rejected', approvedBy: 'admin', approvedAt: new Date().toISOString() } : r)
    setRequests(updated)
    localStorage.setItem('hris_approvals', JSON.stringify(updated))
  }

  const pending = requests.filter(r => r.status === 'pending').length
  const approved = requests.filter(r => r.status === 'approved').length

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <div><h2 className="text-2xl font-bold text-gray-900">Authorization / Approvals</h2><p className="text-sm text-gray-500 mt-1">Approval workflow — cloned from PayrollProSolutions frmAuthorization</p></div>
        <Button onClick={() => setShowModal(true)} icon={<Plus className="h-4 w-4" />}>New Request</Button>
      </div>
      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[['Pending',pending,'yellow',Clock],['Approved',approved,'green',CheckCircle],['Rejected',requests.filter(r=>r.status==='rejected').length,'red',XCircle],['Total',requests.length,'blue',FileText]].map(([l,v,c,Icon]:any)=>(
          <div key={l} className={`rounded-xl border border-${c}-200 bg-${c}-50 p-4`}><div className="flex items-center gap-2">{Icon && <Icon className={`h-4 w-4 text-${c}-600`} />}<p className={`text-xs text-${c}-600`}>{l}</p></div><p className={`mt-1 text-xl font-bold text-${c}-700`}>{v}</p></div>
        ))}
      </div>
      <Card><CardHeader title="Approval Requests" />
        <div className="overflow-x-auto"><table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50"><tr>{['Date','Type','Description','Requested By','Status','Approved By','Actions'].map(h=><th key={h} className="px-3 py-2.5 text-left text-xs font-medium uppercase text-gray-500">{h}</th>)}</tr></thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {requests.length === 0 ? <tr><td colSpan={7} className="py-8 text-center text-gray-400">No approval requests.</td></tr> :
             requests.map(r => (<tr key={r.id} className="hover:bg-gray-50"><td className="px-3 py-2.5 text-xs">{r.createdAt?.slice(0,10)}</td><td className="px-3 py-2.5"><Badge color="blue">{r.type}</Badge></td><td className="px-3 py-2.5 text-xs">{r.description}</td><td className="px-3 py-2.5 text-xs">{r.requestedBy}</td><td className="px-3 py-2.5"><Badge color={r.status==='approved'?'green':r.status==='rejected'?'red':'yellow'}>{r.status}</Badge></td><td className="px-3 py-2.5 text-xs">{r.approvedBy || '—'}</td><td className="px-3 py-2.5">{r.status==='pending' && <div className="flex gap-1"><button onClick={()=>approve(r.id)} className="text-xs text-green-600 hover:underline">Approve</button><button onClick={()=>reject(r.id)} className="text-xs text-red-600 hover:underline">Reject</button></div>}</td></tr>))}
          </tbody>
        </table></div>
      </Card>
      <Modal open={showModal} onClose={() => setShowModal(false)} title="New Approval Request" size="sm" footer={<div className="flex gap-2"><Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button><Button onClick={submit} icon={<FileText className="h-4 w-4"/>}>Submit</Button></div>}>
        <div className="space-y-3">
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Type</label><select value={form.type} onChange={e => setForm({...form, type: e.target.value})} className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">{['Payroll','Overtime','Leave','Bonus','Adjustment'].map(t=><option key={t} value={t}>{t}</option>)}</select></div>
          <Input label="Description" value={form.description} onChange={e => setForm({...form, description: e.target.value})} required />
          <Input label="Requested By" value={form.requestedBy} onChange={e => setForm({...form, requestedBy: e.target.value})} />
        </div>
      </Modal>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════
// 3. Bank File + DOLE Export
// ═══════════════════════════════════════════════════════════════

export function HrisBankExportPage() {
  const [payrolls, setPayrolls] = useState<any[]>([])
  const [message, setMessage] = useState('')

  useEffect(() => {
    ;(async () => {
      try { const p = await payrollRepo.findMany({ page: 1, pageSize: 500 }); setPayrolls('items' in p ? p.items : []) } catch { }
    })()
  }, [])

  function exportBankFile() {
    if (payrolls.length === 0) { setMessage('No payroll data to export.'); return }
    const lines = payrolls.map((p: any) => {
      const name = (p.employeeName || 'Unknown').padEnd(40)
      const amount = (p.netPay ?? 0).toFixed(2).padStart(12)
      return `${name}${amount}`
    })
    const content = lines.join('\n')
    const blob = new Blob([content], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url; a.download = `Payroll_Bank_File_${new Date().toISOString().slice(0,10)}.txt`
    a.click(); URL.revokeObjectURL(url)
    setMessage(`Bank file exported with ${payrolls.length} records.`)
  }

  function exportExcel() {
    if (payrolls.length === 0) { setMessage('No payroll data to export.'); return }
    let csv = 'Employee Name,Department,Gross Pay,SSS,PhilHealth,Pag-IBIG,Withholding Tax,Net Pay\n'
    for (const p of payrolls) {
      csv += `"${p.employeeName || ''}","${p.department || ''}",${p.grossPay || 0},${p.sssContribution || 0},${p.philhealthContribution || 0},${p.pagibigContribution || 0},${p.withholdingTax || 0},${p.netPay || 0}\n`
    }
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url; a.download = `Payroll_Excel_${new Date().toISOString().slice(0,10)}.csv`
    a.click(); URL.revokeObjectURL(url)
    setMessage(`Excel/CSV file exported with ${payrolls.length} records.`)
  }

  function printDOLE() {
    if (payrolls.length === 0) { setMessage('No payroll data. Run payroll first.'); return }
    const w = window.open('', '_blank', 'width=900,height=700')
    if (!w) return
    let rows = ''
    for (const p of payrolls) {
      rows += `<tr><td>${p.employeeName || ''}</td><td>${p.department || ''}</td><td style="text-align:right">₱${(p.basicPay||0).toLocaleString()}</td><td style="text-align:right">₱${(p.overtimePay||0).toLocaleString()}</td><td style="text-align:right">₱${(p.sssContribution||0).toLocaleString()}</td><td style="text-align:right">₱${(p.philhealthContribution||0).toLocaleString()}</td><td style="text-align:right">₱${(p.pagibigContribution||0).toLocaleString()}</td><td style="text-align:right">₱${(p.withholdingTax||0).toLocaleString()}</td><td style="text-align:right;font-weight:bold">₱${(p.netPay||0).toLocaleString()}</td></tr>`
    }
    w.document.write(`<html><head><title>DOLE Payroll Sheet</title><style>body{font-family:Arial;padding:20px}h2{text-align:center}table{width:100%;border-collapse:collapse}th,td{padding:6px 10px;border:1px solid #ccc;font-size:12px}th{background:#f0f0f0}@media print{body{padding:10px}}</style></head><body><h2>Monthly DOLE Payroll Sheet</h2><p>Date: ${new Date().toLocaleDateString()}</p><table><thead><tr><th>Employee</th><th>Dept</th><th>Basic Pay</th><th>Overtime</th><th>SSS</th><th>PhilHealth</th><th>Pag-IBIG</th><th>Tax</th><th>Net Pay</th></tr></thead><tbody>${rows}</tbody></table><p style="font-size:11px;color:#999;margin-top:20px">Generated by CoopERP HRIS — DOLE Payroll Sheet Report</p></body></html>`)
    w.document.close()
    setTimeout(() => w.print(), 300)
    setMessage('DOLE Payroll Sheet opened for printing.')
  }

  return (
    <div className="p-6">
      <div className="mb-6"><h2 className="text-2xl font-bold text-gray-900">Bank File & DOLE Export</h2><p className="text-sm text-gray-500 mt-1">Export payroll data — cloned from PayrollProSolutions payCardBankFile, payCardExcelFile, payrollSheetForDOLE</p></div>

      {message && <div className="mb-4 rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-700">{message}</div>}

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
        <Card>
          <CardHeader title="Bank File" description="Fixed-width text format for bank upload" />
          <div className="p-6 text-center">
            <Building2 className="h-12 w-12 mx-auto mb-4 text-blue-400" />
            <p className="text-sm text-gray-500 mb-4">Export payroll as fixed-width bank file (.txt)</p>
            <Button onClick={exportBankFile} icon={<Download className="h-4 w-4" />} className="w-full">Export Bank File</Button>
          </div>
        </Card>

        <Card>
          <CardHeader title="Excel Export" description="CSV format for Excel import" />
          <div className="p-6 text-center">
            <FileText className="h-12 w-12 mx-auto mb-4 text-green-400" />
            <p className="text-sm text-gray-500 mb-4">Export payroll as CSV for Excel (.csv)</p>
            <Button onClick={exportExcel} icon={<Download className="h-4 w-4" />} className="w-full">Export Excel/CSV</Button>
          </div>
        </Card>

        <Card>
          <CardHeader title="DOLE Payroll Sheet" description="Government-mandated format" />
          <div className="p-6 text-center">
            <Printer className="h-12 w-12 mx-auto mb-4 text-red-400" />
            <p className="text-sm text-gray-500 mb-4">Monthly DOLE Payroll Sheet for compliance</p>
            <Button onClick={printDOLE} icon={<Printer className="h-4 w-4" />} className="w-full">Print DOLE Sheet</Button>
          </div>
        </Card>
      </div>

      <div className="mt-6">
        <Card><CardHeader title="Payroll Data Preview" description={`${payrolls.length} records available for export`} />
          <div className="overflow-x-auto max-h-96 overflow-y-auto"><table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50"><tr>{['Employee','Gross','SSS','PhilHealth','Pag-IBIG','Tax','Net Pay'].map(h=><th key={h} className="px-3 py-2.5 text-left text-xs font-medium uppercase text-gray-500">{h}</th>)}</tr></thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {payrolls.slice(0, 20).map((p: any) => (<tr key={p.id} className="hover:bg-gray-50"><td className="px-3 py-2.5 text-xs font-medium">{p.employeeName ?? p.employeeId?.slice(0,8)}</td><td className="px-3 py-2.5 text-right">₱{(p.grossPay??0).toLocaleString()}</td><td className="px-3 py-2.5 text-right text-xs">₱{(p.sssContribution??0).toLocaleString()}</td><td className="px-3 py-2.5 text-right text-xs">₱{(p.philhealthContribution??0).toLocaleString()}</td><td className="px-3 py-2.5 text-right text-xs">₱{(p.pagibigContribution??0).toLocaleString()}</td><td className="px-3 py-2.5 text-right text-xs">₱{(p.withholdingTax??0).toLocaleString()}</td><td className="px-3 py-2.5 text-right font-bold">₱{(p.netPay??0).toLocaleString()}</td></tr>))}
              {payrolls.length === 0 && <tr><td colSpan={7} className="py-8 text-center text-gray-400">No payroll data. Run payroll first.</td></tr>}
            </tbody>
          </table></div>
        </Card>
      </div>
    </div>
  )
}
